if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.mainForm = this.productSection?.querySelector('.js-product-form-main');
      this.purchaseArea = this.productSection?.querySelector('.bcm6-stoq-purchase-area') || this.mainForm;
      this.stickyQuantity = this.querySelector('[data-sticky-atc-quantity]');
      this.mainQuantity = this.mainForm?.querySelector('input[name="quantity"], .qty-input__input');
      this.optionsLink = this.querySelector('[data-sticky-atc-options]');
      this.rafId = null;
      this.inputActive = false;
      this.observer = null;

      this.onViewportChange = this.onViewportChange.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onVisibilitySignal = this.onVisibilitySignal.bind(this);
      this.onMainQuantityChange = this.onMainQuantityChange.bind(this);
      this.onOptionsClick = this.onOptionsClick.bind(this);

      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('pageshow', this.onViewportChange);
      document.removeEventListener('visibilitychange', this.onVisibilitySignal);
      document.removeEventListener('focusin', this.onFocusIn);
      document.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('dispatch:cart-drawer:open', this.onVisibilitySignal);
      document.removeEventListener('dispatch:cart-drawer:close', this.onVisibilitySignal);
      document.removeEventListener('on:variant:change', this.onVisibilitySignal);
      document.removeEventListener('shopify:variant:change', this.onVisibilitySignal);
      this.mainQuantity?.removeEventListener('change', this.onMainQuantityChange);
      this.mainQuantity?.removeEventListener('input', this.onMainQuantityChange);
      this.optionsLink?.removeEventListener('click', this.onOptionsClick);
      this.observer?.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.setBodyVisibility(false);
    }

    init() {
      if (!this.productSection || !this.mainForm || !this.purchaseArea) {
        this.classList.add('sticky-atc-panel--out');
        return;
      }

      this.syncQuantity();
      this.bindEvents();
      this.scheduleUpdate();
    }

    bindEvents() {
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      window.addEventListener('pageshow', this.onViewportChange);
      document.addEventListener('visibilitychange', this.onVisibilitySignal);
      document.addEventListener('focusin', this.onFocusIn);
      document.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('dispatch:cart-drawer:open', this.onVisibilitySignal);
      document.addEventListener('dispatch:cart-drawer:close', this.onVisibilitySignal);
      document.addEventListener('on:variant:change', this.onVisibilitySignal);
      document.addEventListener('shopify:variant:change', this.onVisibilitySignal);

      this.mainQuantity?.addEventListener('change', this.onMainQuantityChange);
      this.mainQuantity?.addEventListener('input', this.onMainQuantityChange);
      this.optionsLink?.addEventListener('click', this.onOptionsClick);

      if ('MutationObserver' in window) {
        this.observer = new MutationObserver(() => this.scheduleUpdate());
        this.observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['class']
        });
        document.querySelectorAll('cart-drawer, quick-add-drawer, dialog, [role="dialog"], .js-overlay').forEach((element) => {
          this.observer.observe(element, {
            attributes: true,
            attributeFilter: ['class', 'open', 'aria-hidden']
          });
        });
      }
    }

    onViewportChange() {
      this.scheduleUpdate();
    }

    onVisibilitySignal() {
      window.setTimeout(() => this.scheduleUpdate(), 0);
    }

    onMainQuantityChange() {
      this.syncQuantity();
    }

    onFocusIn(event) {
      const target = event.target;
      if (!target || this.contains(target)) return;
      if (target.matches('input, textarea, select, [contenteditable="true"]')) {
        this.inputActive = true;
        this.scheduleUpdate();
      }
    }

    onFocusOut() {
      window.setTimeout(() => {
        const active = document.activeElement;
        this.inputActive = Boolean(
          active
          && !this.contains(active)
          && active.matches('input, textarea, select, [contenteditable="true"]')
        );
        this.scheduleUpdate();
      }, 120);
    }

    onOptionsClick(event) {
      const targetId = this.optionsLink?.getAttribute('href');
      if (!targetId || !targetId.startsWith('#')) return;
      const target = document.querySelector(targetId);
      if (!target) return;

      event.preventDefault();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      window.setTimeout(() => {
        const focusTarget = this.productSection.querySelector(
          '.js-product-form-main [name="add"], variant-picker input, variant-picker button, .product-info__add-to-cart button'
        );
        focusTarget?.focus({ preventScroll: true });
      }, reducedMotion ? 0 : 420);
    }

    syncQuantity() {
      if (!this.stickyQuantity || !this.mainQuantity) return;
      const value = Number.parseInt(this.mainQuantity.value, 10);
      this.stickyQuantity.value = Number.isFinite(value) && value > 0 ? value : 1;
    }

    scheduleUpdate() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.updateVisibility();
      });
    }

    updateVisibility() {
      if (!this.isConnected || document.visibilityState === 'hidden') return;

      const purchaseRect = this.purchaseArea.getBoundingClientRect();
      const hasPassedPurchaseArea = purchaseRect.bottom <= 0;
      const nearPageBottom = window.scrollY + window.innerHeight + 140 >= document.documentElement.scrollHeight;
      const suppressed = this.inputActive || this.hasOpenOverlay();
      const shouldShow = hasPassedPurchaseArea && !nearPageBottom && !suppressed;

      this.classList.toggle('sticky-atc-panel--out', !shouldShow);
      document.body.classList.toggle('aonsk-sticky-atc-suppressed', suppressed);
      document.body.classList.toggle('aonsk-sticky-atc-visible', shouldShow);
      document.body.classList.toggle('scrolled-to-bottom', nearPageBottom);
      this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }

    hasOpenOverlay() {
      if (document.body.classList.contains('bcm6-cart-drawer-open')) return true;
      if (document.documentElement.classList.contains('bcm6-cart-drawer-open')) return true;

      const openOverlay = document.querySelector([
        'dialog[open]',
        'quick-add-drawer[aria-hidden="false"]',
        '[role="dialog"][aria-hidden="false"]',
        '.js-overlay.is-visible',
        '.media-viewer[open]',
        '.lightbox[open]'
      ].join(','));

      return Boolean(openOverlay && !this.contains(openOverlay));
    }

    setBodyVisibility(visible) {
      if (!document.body) return;
      if (!visible && !document.querySelector('sticky-atc-panel:not(.sticky-atc-panel--out)')) {
        document.body.classList.remove('aonsk-sticky-atc-visible');
      }
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
